import React, { useState, useCallback } from 'react';
import { Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface FileUploadDropzoneProps {
  onFileSelect?: (file: File) => void;
  onFilesSelect?: (files: File[]) => void;
  accept?: string;
  maxSize?: number; // in MB
  multiple?: boolean;
  className?: string;
}

interface FileWithPreview {
  file: File;
  preview: string;
}

export const FileUploadDropzone: React.FC<FileUploadDropzoneProps> = ({
  onFileSelect,
  onFilesSelect,
  accept = 'image/png,image/jpeg,image/jpg,image/webp,image/gif',
  maxSize = 10,
  multiple = false,
  className
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [previews, setPreviews] = useState<FileWithPreview[]>([]);
  const [error, setError] = useState<string | null>(null);

  const validateAndProcessFiles = useCallback((fileList: File[]) => {
    setError(null);
    const validFiles: File[] = [];
    const newPreviews: FileWithPreview[] = [];
    const acceptedTypes = accept.split(',').map(t => t.trim());

    for (const file of fileList) {
      // Validate file type
      if (!acceptedTypes.includes(file.type)) {
        setError(`Invalid file type: ${file.name}. Accepted: ${accept}`);
        continue;
      }

      // Validate file size
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxSize) {
        setError(`File too large: ${file.name}. Max size: ${maxSize}MB`);
        continue;
      }

      validFiles.push(file);

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const preview = e.target?.result as string;
          newPreviews.push({ file, preview });
          setPreviews(prev => [...prev, { file, preview }]);
        };
        reader.readAsDataURL(file);
      }
    }

    if (validFiles.length === 0) {
      return;
    }

    // Call appropriate callback
    if (multiple && onFilesSelect) {
      onFilesSelect(validFiles);
    } else if (!multiple && onFileSelect && validFiles.length > 0) {
      onFileSelect(validFiles[0]);
      // For single file mode, only keep one preview
      setPreviews([]);
    }
  }, [accept, maxSize, multiple, onFileSelect, onFilesSelect]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);

    if (!multiple && files.length > 1) {
      setError('Only one file allowed. Please select a single file.');
      return;
    }

    validateAndProcessFiles(files);
  }, [multiple, validateAndProcessFiles]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];

    if (!multiple && files.length > 1) {
      setError('Only one file allowed. Please select a single file.');
      return;
    }

    validateAndProcessFiles(files);
  };

  const removeFile = (index: number) => {
    setPreviews(prev => prev.filter((_, i) => i !== index));

    // If all files removed, clear error
    if (previews.length === 1) {
      setError(null);
    }
  };

  const clearAll = () => {
    setPreviews([]);
    setError(null);
  };

  return (
    <div className={cn("w-full", className)}>
      {previews.length > 0 ? (
        <div className="space-y-4">
          {/* Preview Grid */}
          <div className={cn(
            "grid gap-4",
            previews.length === 1 ? "grid-cols-1" : "grid-cols-2 md:grid-cols-3"
          )}>
            {previews.map((item, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <div className="relative">
                    <img
                      src={item.preview}
                      alt={item.file.name}
                      className={cn(
                        "w-full object-cover rounded",
                        previews.length === 1 ? "h-64" : "h-32"
                      )}
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 h-6 w-6"
                      onClick={() => removeFile(index)}
                      type="button"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    <p className="font-medium truncate">{item.file.name}</p>
                    <p>{(item.file.size / 1024).toFixed(1)} KB</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Clear All Button */}
          {multiple && previews.length > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAll}
              type="button"
              className="w-full"
            >
              Clear All ({previews.length} files)
            </Button>
          )}
        </div>
      ) : (
        <div
          className={cn(
            "border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors",
            isDragging && "border-primary bg-primary/5",
            error && "border-destructive"
          )}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          <input
            id="file-input"
            type="file"
            accept={accept}
            multiple={multiple}
            className="hidden"
            onChange={handleFileInput}
          />
          <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-lg font-medium mb-2">
            {isDragging
              ? `Drop your ${multiple ? 'images' : 'image'} here`
              : `Drag and drop or click to select${multiple ? ' (multiple files)' : ''}`
            }
          </p>
          <p className="text-sm text-muted-foreground">
            Accepted formats: PNG, JPEG, JPG, WebP, GIF | Max size: {maxSize}MB per file
          </p>
          {error && (
            <p className="text-sm text-destructive mt-2">{error}</p>
          )}
        </div>
      )}
    </div>
  );
};
